import { Button, Paper, Stepper, Step, Box, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback } from 'react'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '../../../Hooks/useCustomTranslation'
import OptionalTooltipWrapper from '../../Tooltip/OptionalTooltipWrapper'
import useHorizontalScroll from '../../../Hooks/scrolling/useHorizontalScroll'
import { WizardStepModel } from '../types'
import WizardStepContent from './WizardStepContent'

interface StyleProps {
  isButtonDisabled: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  stepBar: {
    position: 'absolute',
    left: 0,
    top: '100%',
    width: '100%',
    height: '60px',
    borderRadius: '0',
    borderTop: `1px solid ${theme.palette.grey.grey06}`,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between',
      alignItems: undefined,
      margin: 0,
    },
  },
  stepper: {
    position: 'relative',
    paddingRight: theme.spacing(2),

    '.MuiStepConnector-root': {
      width: 24,
      flex: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      gap: 3,
      display: 'flex',
      justifyContent: 'center',
      paddingRight: 0,
    },
  },
  step: {
    padding: 0,
  },
  rightButton: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    right: 0,
    cursor: props.isButtonDisabled ? 'not-allowed' : 'normal',
    paddingRight: theme.spacing(2),
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
    button: {
      margin: 0,
    },
    '&:before': {
      content: '""',
      position: 'absolute',
      left: `-${theme.spacing(4)}`,
      right: 0,
      top: -5,
      height: 'calc(100% + 10px)',
      background: `linear-gradient(to right, rgba(255,255,255,0.05) 0%, rgba(255,255,255,1) ${theme.spacing(4)})`,
      pointerEvents: 'none',
      [theme.breakpoints.down('sm')]: {
        left: 0,
      },
    },
  },
  leftButton: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    right: 0,
    cursor: props.isButtonDisabled ? 'not-allowed' : 'normal',
    paddingLeft: theme.spacing(2),
    button: {
      margin: 0,
    },
    '&:before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: -5,
      height: 'calc(100% + 10px)',
      background: `linear-gradient(to right, rgba(255,255,255,0.05) 0%, rgba(255,255,255,1) ${theme.spacing(4)})`,
      pointerEvents: 'none',
    },
  },
}))

interface Props {
  steps: WizardStepModel[]
  activeStep: number
  setActiveStep: (activeStep: number) => void
  isLastStepValid: boolean
  onConfirm: () => void
}

/**
 * Displays the stepper of a wizard which allows to move between the steps
 */
const WizardStepper: React.VFC<Props> = ({ steps, activeStep, setActiveStep, onConfirm, isLastStepValid }) => {
  const areThereAnyValidationErrors = steps.some(s => !s.hidden && s.buttonType === 'invalid')
  const isLastStep = activeStep === steps.length - 1
  const isFirstStep = activeStep === 0
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { classes } = useStyles({ isButtonDisabled: areThereAnyValidationErrors })
  const { t } = useCustomTranslation()
  const containerRef = useHorizontalScroll()

  const scrollToStep = (node: HTMLElement): void => {
    if (node && containerRef.current) {
      containerRef.current.scrollTo({
        left: node.offsetLeft - 50,
        behavior: 'smooth',
      })
    }
  }

  const handleStepClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    if (!e.currentTarget.classList.contains('MuiStep-root')) {
      return
    }
    scrollToStep(e.currentTarget)
  }

  const onClickStep = (stepIndex: number): void => {
    if (areThereAnyValidationErrors) {
      return
    }

    setActiveStep(stepIndex)
  }

  const onNextStepButton = useCallback(
    (isLastStep: boolean) => {
      if (isLastStep) {
        onConfirm()
        return
      }

      const nextStepIndex = steps.findIndex((step, index) => !step.hidden && index > activeStep)
      setActiveStep(nextStepIndex)
    },
    [activeStep, setActiveStep, steps, onConfirm]
  )

  const onPreviousStepButton = (): void => {
    const currentStepNumber = steps[activeStep].stepNumber
    const previousStepIndex = currentStepNumber !== undefined ? steps.findIndex(step => step.stepNumber === currentStepNumber - 1) : 0

    setActiveStep(previousStepIndex)
  }

  const nextButtonDisabled = isLastStep ? !isLastStepValid : areThereAnyValidationErrors
  const previousButtonDisabled = isFirstStep ? true : areThereAnyValidationErrors

  return (
    <Paper className={classes.stepBar}>
      {isMobile && (
        <Box className={classes.leftButton}>
          <Button sx={{ minWidth: 100 }} color="primary" variant="contained" onClick={() => onPreviousStepButton()} disabled={previousButtonDisabled}>
            {t('common.back')}
          </Button>
        </Box>
      )}
      <Stepper activeStep={activeStep} ref={containerRef} nonLinear={true} className={classes.stepper} connector={isMobile ? null : undefined}>
        {steps.map((step, index) =>
          step.hidden ? null : (
            <Step key={index} className={classes.step} onClick={handleStepClick}>
              <WizardStepContent
                type={step.buttonType}
                isActive={activeStep === index}
                step={step}
                stepIndex={index}
                isSelectable={!areThereAnyValidationErrors}
                onClick={onClickStep}
              />
            </Step>
          )
        )}
      </Stepper>
      <Box className={classes.rightButton}>
        <OptionalTooltipWrapper hasTooltip={steps[activeStep].buttonType === 'invalid'} titleKey={steps[activeStep].validationTooltipTitle}>
          <Button
            sx={{ mr: 2, minWidth: 100 }}
            color="primary"
            variant="contained"
            onClick={() => onNextStepButton(isLastStep)}
            disabled={nextButtonDisabled}
          >
            {isLastStep ? t('common.send') : t('common.next')}
          </Button>
        </OptionalTooltipWrapper>
      </Box>
    </Paper>
  )
}

export default WizardStepper
